/**
 * Request Documents Modal
 * =======================
 * Modal for compliance officers to request additional documents from applicants.
 */

import React, { useState } from 'react';
import {
  FileText,
  X,
  Send,
  Copy,
  Check,
  Loader2,
  Calendar,
  MessageSquare,
  AlertCircle,
} from 'lucide-react';
import { useCreateDocumentRequest } from '../hooks/useDocumentRequests';

const DOCUMENT_TYPES = [
  { value: 'proof_of_address', label: 'Proof of Address', description: 'Utility bill, bank statement, or government letter' },
  { value: 'bank_statement', label: 'Bank Statement', description: 'Recent bank statement (last 3 months)' },
  { value: 'utility_bill', label: 'Utility Bill', description: 'Gas, electric, water, or internet bill' },
  { value: 'payslip', label: 'Payslip', description: 'Recent payslip or salary statement' },
  { value: 'tax_return', label: 'Tax Return', description: 'Most recent tax return or assessment' },
  { value: 'source_of_funds', label: 'Source of Funds', description: 'Documentation showing origin of funds' },
  { value: 'additional_id', label: 'Additional ID', description: 'Secondary form of identification' },
  { value: 'selfie', label: 'New Selfie', description: 'Fresh selfie photo for verification' },
  { value: 'other', label: 'Other Document', description: 'Any other document' },
];

const EXPIRY_OPTIONS = [
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

const RECENCY_OPTIONS = [
  { value: null, label: 'No requirement' },
  { value: 30, label: 'Last 30 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 6 months' },
];

export default function RequestDocsModal({ isOpen, onClose, applicantId, applicantName }) {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [reason, setReason] = useState('');
  const [instructions, setInstructions] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [recencyDays, setRecencyDays] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const createRequest = useCreateDocumentRequest();

  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async () => {
    if (selectedTypes.length === 0) return;

    try {
      const data = await createRequest.mutateAsync({
        applicantId,
        documentTypes: selectedTypes,
        reason: reason || undefined,
        instructions: instructions || undefined,
        expiresInDays,
        recencyDays: recencyDays || undefined,
      });
      setResult(data);
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleCopy = async () => {
    if (result?.upload_url) {
      await navigator.clipboard.writeText(result.upload_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setSelectedTypes([]);
    setReason('');
    setInstructions('');
    setExpiresInDays(7);
    setRecencyDays(null);
    setResult(null);
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: '16px',
          border: '1px solid var(--border-color)',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-tertiary))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <FileText size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Request Documents</h2>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
                {applicantName || 'Applicant'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              padding: '8px',
              borderRadius: '8px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {result ? (
            // Success state - show link
            <div>
              <div
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px',
                  textAlign: 'center',
                }}
              >
                <Check
                  size={40}
                  style={{ color: '#10b981', marginBottom: '12px' }}
                />
                <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600 }}>
                  Request Created
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)' }}>
                  Share this link with the applicant to collect documents.
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', display: 'block' }}>
                  SECURE UPLOAD LINK
                </label>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'var(--bg-tertiary)',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <input
                    type="text"
                    readOnly
                    value={result.upload_url}
                    style={{
                      flex: 1,
                      background: 'none',
                      border: 'none',
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      color: 'var(--text-primary)',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleCopy}
                    style={{
                      background: copied ? 'rgba(16, 185, 129, 0.15)' : 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      color: copied ? '#10b981' : 'var(--text-primary)',
                    }}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div
                style={{
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  padding: '16px',
                }}
              >
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600 }}>
                  Request Details
                </h4>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <p style={{ margin: '0 0 8px' }}>
                    <strong>Documents:</strong> {result.document_types.join(', ')}
                  </p>
                  <p style={{ margin: '0 0 8px' }}>
                    <strong>Expires:</strong> {new Date(result.expires_at).toLocaleDateString()}
                  </p>
                  {result.reason && (
                    <p style={{ margin: '0 0 8px' }}>
                      <strong>Reason:</strong> {result.reason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Form state
            <>
              {/* Document Types */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', display: 'block' }}>
                  Select Documents to Request
                </label>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {DOCUMENT_TYPES.map((type) => (
                    <div
                      key={type.value}
                      onClick={() => toggleType(type.value)}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: `1px solid ${selectedTypes.includes(type.value) ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        background: selectedTypes.includes(type.value)
                          ? 'rgba(139, 92, 246, 0.1)'
                          : 'var(--bg-tertiary)',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          border: `2px solid ${selectedTypes.includes(type.value) ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                          background: selectedTypes.includes(type.value)
                            ? 'var(--accent-primary)'
                            : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {selectedTypes.includes(type.value) && (
                          <Check size={12} color="white" />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>{type.label}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {type.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expiry */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={14} />
                  Link Expiry
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {EXPIRY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setExpiresInDays(opt.value)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: `1px solid ${expiresInDays === opt.value ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        background: expiresInDays === opt.value
                          ? 'rgba(139, 92, 246, 0.1)'
                          : 'var(--bg-tertiary)',
                        color: expiresInDays === opt.value ? 'var(--accent-primary)' : 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Document Recency Requirement */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={14} />
                  Document Recency Requirement
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                  Require the document to be dated within a specific timeframe
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {RECENCY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value || 'none'}
                      onClick={() => setRecencyDays(opt.value)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: `1px solid ${recencyDays === opt.value ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        background: recencyDays === opt.value
                          ? 'rgba(139, 92, 246, 0.1)'
                          : 'var(--bg-tertiary)',
                        color: recencyDays === opt.value ? 'var(--accent-primary)' : 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reason */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertCircle size={14} />
                  Reason (Optional)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Address verification required"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                  }}
                />
              </div>

              {/* Instructions */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MessageSquare size={14} />
                  Instructions (Optional)
                </label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Any specific instructions for the applicant..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    resize: 'vertical',
                  }}
                />
              </div>

              {createRequest.error && (
                <div
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#ef4444',
                  }}
                >
                  {createRequest.error.message}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          {result ? (
            <button
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--accent-primary)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={handleClose}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={selectedTypes.length === 0 || createRequest.isPending}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: selectedTypes.length === 0 ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
                  color: selectedTypes.length === 0 ? 'var(--text-muted)' : 'white',
                  cursor: selectedTypes.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {createRequest.isPending ? (
                  <>
                    <Loader2 size={14} className="spinner" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Create Request
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
